import type { WidgetConfig, Message, QuickReply } from '../types';
import { h } from '../utils/dom';
import { Header } from './header';
import { MessagesView } from './messages';
import { Composer } from './composer';
import { QuickRepliesView } from './quick-replies';
import { createHandoffCard } from './handoff';

type PanelCallbacks = {
  onMinimize: () => void;
  onClose: () => void;
  onSend: (text: string) => void;
  onQuickReplyMessage: (content: string) => void;
  onWhatsApp: (value: string, label: string) => void;
};

export class Panel {
  el: HTMLDivElement;
  private messagesView: MessagesView;
  private composer: Composer;
  private quickRepliesView: QuickRepliesView | null = null;
  private config: WidgetConfig;

  constructor(config: WidgetConfig, callbacks: PanelCallbacks) {
    this.config = config;
    const cfg = config.config;

    this.el = h('div', {
      class: 'att-panel',
      role: 'dialog',
      'aria-modal': 'false',
      'aria-label': `Chat - ${cfg.brandName}`,
    }) as HTMLDivElement;

    // Header
    const header = new Header(config, callbacks.onMinimize, callbacks.onClose);
    this.el.appendChild(header.el);

    // Messages
    this.messagesView = new MessagesView(
      config.team,
      cfg.msgWelcome,
      // Sem atendente online/fora do horário: avisa já na abertura (a mensagem ainda é entregue)
      !config.status.online ? cfg.msgOffline : null,
    );
    this.el.appendChild(this.messagesView.el);

    // Quick replies (if any)
    if (cfg.quickReplies.length > 0) {
      this.quickRepliesView = new QuickRepliesView(cfg.quickReplies, {
        onMessage: callbacks.onQuickReplyMessage,
        onWhatsApp: callbacks.onWhatsApp,
        onLink: (_url: string, label: string) => {
          callbacks.onSend(`[escolheu: ${label}]`);
        },
      });
      this.el.appendChild(this.quickRepliesView.el);
    }

    // Composer
    this.composer = new Composer(callbacks.onSend);
    this.el.appendChild(this.composer.el);

    // Footer (powered by)
    if (cfg.brandingShowPoweredBy) {
      const footer = h('div', { class: 'att-footer' });
      const link = h('a', {
        href: 'https://attende.digital',
        target: '_blank',
        rel: 'noopener noreferrer',
      }, 'powered by attende');
      footer.appendChild(link);
      this.el.appendChild(footer);
    }

    // Focus trap
    this.el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        this.handleFocusTrap(e);
      }
    });
  }

  private handleFocusTrap(e: KeyboardEvent): void {
    const focusable = Array.from(
      this.el.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  open(): void {
    this.el.classList.add('att-panel--open');
  }

  close(): void {
    this.el.classList.remove('att-panel--open');
  }

  renderMessages(messages: Message[]): void {
    this.messagesView.renderMessages(messages);
  }

  appendMessage(msg: Message): void {
    this.messagesView.appendMessage(msg);
  }

  setMessageStatus(id: number, status: 'sending' | 'sent' | 'failed', onRetry?: () => void, reason?: string): void {
    this.messagesView.setMessageStatus(id, status, onRetry, reason);
  }

  replaceMessageId(oldId: number, newId: number): void {
    this.messagesView.replaceMessageId(oldId, newId);
  }

  appendNotice(text: string): void {
    this.messagesView.appendNotice(text);
  }

  showTyping(): void {
    this.messagesView.showTyping();
  }

  hideTyping(): void {
    this.messagesView.hideTyping();
  }

  showError(onRetry: () => void): void {
    this.messagesView.showError(onRetry);
    this.composer.setDisabled(true);
  }

  focusComposer(): void {
    this.composer.focus();
  }

  /** [B4-13] Desabilita/habilita o composer para evitar duplo envio durante request */
  setComposerDisabled(disabled: boolean): void {
    this.composer.setDisabled(disabled);
  }

  appendHandoffCard(waUrl: string): void {
    const card = createHandoffCard(waUrl, this.config.config.msgHandoff);
    // Insert into messages area
    const row = h('div', { class: 'att-msg-row att-msg-row--agent' });
    const avatarWrap = h('div', { class: 'att-msg-avatar-wrap' });
    row.appendChild(avatarWrap);
    row.appendChild(card);
    this.messagesView.el.appendChild(row);
    this.messagesView.scrollToBottom();
  }

  hideQuickReplies(): void {
    this.quickRepliesView?.hide();
  }

  get quickReplies(): QuickReply[] {
    return this.config.config.quickReplies;
  }
}
